import { useState, useEffect } from "react";
import { Credentials, retrieveFileMetadata } from "../utils/fileUtils";

export interface FileMetadata {
  fileName: string;
  mimeType?: string;
  lastUpdated?: number; // Unix timestamp in milliseconds
  metadata?: Record<string, string>;
}

/**
 * Hook to fetch only file metadata from CDF (without downloading content)
 */
export const useFetchFileMetadata = (
  appId: string | undefined,
  credentials: Credentials | null,
  retrieveFileMetadataFunction: (
    externalId: string,
    credentials: Credentials,
  ) => Promise<any> = retrieveFileMetadata,
): {
  fileMetadata: FileMetadata | undefined;
  isLoading: boolean;
  error: Error | null;
} => {
  const [fileMetadata, setFileMetadata] = useState<FileMetadata | undefined>(
    undefined,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let isActive = true;

    const fetchMetadata = async () => {
      if (!appId || !credentials) {
        setFileMetadata(undefined);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Get file metadata only (no download)
        const file = await retrieveFileMetadataFunction(appId, credentials);

        if (isActive) {
          setFileMetadata({
            fileName: file.name,
            mimeType: file.mimeType,
            lastUpdated: file.lastUpdatedTime,
            metadata: file.metadata,
          });
        }
      } catch (err) {
        if (isActive) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    fetchMetadata();

    return () => {
      isActive = false;
    };
  }, [appId, credentials, retrieveFileMetadataFunction]);

  return {
    fileMetadata,
    isLoading,
    error,
  };
};
